export const parseLabeledInput = <K extends string>(rawInput: string, schemaFieldKey: K, schemaLabel: string): Record<K, string> => {
  if (!rawInput) {
    return { [schemaFieldKey]: "" } as Record<K, string>;
  }

  const trimmedInput = rawInput.trim();

  // Return early if label is empty or not provided
  if (!schemaLabel) {
    return { [schemaFieldKey]: trimmedInput } as Record<K, string>;
  }

  const escapedLabel = escapeRegex(schemaLabel);
  const separatorPattern = "[\\s:：\u3000]*";
  const labelPattern = new RegExp(`^${escapedLabel}${separatorPattern}`, "u");

  // Strip label and separators if present
  const parsedValue = trimmedInput.replace(labelPattern, "").trim();

  return { [schemaFieldKey]: parsedValue } as Record<K, string>;
};

const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const parseLabeledInputBatch = <K extends string>(
  inputs: Array<{
    rawInput: string;
    schemaFieldKey: K;
    schemaLabel: string;
  }>,
): Record<K, string> => {
  return inputs.reduce(
    (acc, { rawInput, schemaFieldKey, schemaLabel }) => {
      const parsed = parseLabeledInput(rawInput, schemaFieldKey, schemaLabel);
      return { ...acc, ...parsed };
    },
    {} as Record<K, string>,
  );
};

export const stripLabelFromValue = (value: string | null | undefined, fieldKey: string, labelMap: Record<string, string>): string | null => {
  if (!value || typeof value !== "string") return value ?? null;

  const label = labelMap[fieldKey];
  if (!label) return value;

  const result = parseLabeledInput(value, fieldKey, label);
  return result[fieldKey] || null;
};

export const stripLabelsFromObject = <T extends Record<string, unknown>>(data: T, labelMap: Record<string, string>): T => {
  if (!data || typeof data !== "object") return data;

  const result = { ...data };

  for (const key of Object.keys(result)) {
    const value = result[key];

    // Only process string values
    if (typeof value === "string") {
      const label = labelMap[key];
      if (label) {
        const parsed = parseLabeledInput(value, key, label);
        (result as Record<string, unknown>)[key] = parsed[key];
      }
    }
  }

  return result;
};

const ARRAY_FIELDS = ["specific_skill_certificates"];

export const parseMemoToObject = (
  memo: string | null | undefined,
  labelMap: Record<string, string | string[]>,
): Record<string, string | string[]> => {
  if (!memo) return {};

  const result: Record<string, string | string[]> = {};

  // Create reverse map: label → fieldKey (supports array of labels/aliases)
  const labelToFieldKey: Record<string, string> = {};
  for (const [fieldKey, labelOrLabels] of Object.entries(labelMap)) {
    if (labelOrLabels) {
      const labels = Array.isArray(labelOrLabels) ? labelOrLabels : [labelOrLabels];
      for (const label of labels) {
        if (label) {
          labelToFieldKey[label] = fieldKey;
        }
      }
    }
  }

  // Sort labels by length (longest first) to avoid partial matches
  const sortedLabels = Object.keys(labelToFieldKey).sort((a, b) => b.length - a.length);

  // Process each line
  const lines = memo.split(/\r?\n/);

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    // Try to match each label
    for (const label of sortedLabels) {
      const escapedLabel = escapeRegex(label);
      // Match label followed by optional separators (colon, space, full-width)
      const regex = new RegExp(`^${escapedLabel}[\\s:：\u3000]*(.*)$`, "u");
      const match = trimmedLine.match(regex);

      if (match) {
        const fieldKey = labelToFieldKey[label];
        const value = match[1].trim();
        if (value) {
          result[fieldKey] = ARRAY_FIELDS.includes(fieldKey) ? [value] : value;
        }
        break;
      }
    }
  }

  return result;
};
